import { INITIAL_REFERRAL_FRIEND_COUNT, REFERRAL_ROUTE } from '@constant/referral';
import { disableLoader, enableLoader } from '@context/actions';
import { getOrderByTenantIdAndGuestId } from '@storeData/order';
import { getReferralConfig, submitGuestReferralInvites } from '@storeData/referral';
import { getUserByTenantAndMobile } from '@storeData/user';
import { GuestReferralInvitePayload, ReferralConfig, ReferralFriendInput } from '@type/referral';
import { navigateTo } from '@util/routerService';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import PhoneInput from 'react-phone-input-2';
import { useDispatch, useSelector } from 'react-redux';

import {
  CloseIcon,
  IconFrame,
  ReferralNetworkIcon,
} from '@element/icons';
const createEmptyFriend = (countryCode = '', index = 0): ReferralFriendInput => ({
    id: `referral-friend-${index}-${Date.now()}-${Math.random()}`,
    name: '',
    mobileNo: '',
    countryCode: countryCode?.toString() || '',
});

const createInitialFriends = (countryCode = '', count = INITIAL_REFERRAL_FRIEND_COUNT): ReferralFriendInput[] =>
    Array.from({ length: count }, (_, index) => createEmptyFriend(countryCode, index));

function AddReferralPage() {
    const dispatch = useDispatch();
    const [cookie] = useCookies();
    const [userData] = useState(cookie['user']);
    const store = useSelector((state: any) => state);
    const storeMetaData = useSelector((state: any) => state.store ? state.store.storeMetaData : null);
    const configData = useSelector((state: any) => state.store?.storeData?.configData);
    const basicConfig = configData?.storeConfig?.basicConfig;
    const languageData: any = store.languageKeywords;
    const defaultCountryCode = useMemo(
        () => storeMetaData?.countryCode?.toString() || '',
        [storeMetaData?.countryCode]
    );
    const defaultCountry = useMemo(
        () => (storeMetaData?.code || 'in').toLowerCase(),
        [storeMetaData?.code]
    );
    const isPhoneSetupReady = Boolean(defaultCountryCode && defaultCountry);
    const currencySymbol = configData?.currencySymbol || '';
    const [referralConfig, setReferralConfig] = useState<ReferralConfig | null>(null);
    const [friends, setFriends] = useState<ReferralFriendInput[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    // friendId -> mobileNo known to already exist in CRM
    const [existingGuestMobiles, setExistingGuestMobiles] = useState<Record<string, string>>({});
    const guestCheckRequestRef = useRef<Record<string, string>>({});

    const tenantId = storeMetaData?.tenantId ?? configData?.tenantId ?? userData?.tenantId;
    const storeId = storeMetaData?.id ?? configData?.storeId ?? userData?.storeId;

    const referrerBenefit = useMemo(() => {
        if (!referralConfig) return '';

        const amount = referralConfig.referredByAmount ?? referralConfig.referredByMaxBenefitAmount;
        if (amount != null && !Number.isNaN(Number(amount))) {
            return `${currencySymbol}${amount}`;
        }

        if (referralConfig.referredByPercentage != null && !Number.isNaN(Number(referralConfig.referredByPercentage))) {
            return `${referralConfig.referredByPercentage}%`;
        }

        return '';
    }, [currencySymbol, referralConfig]);

    const referralOfferMessage = useMemo(() => {
        if (!referrerBenefit) {
            return languageData["Refer your friend/family and when they visit us, get benefit on each referral"]
                || "Refer your friend/family and when they visit us, get benefit on each referral";
        }

        return languageData[`Refer your friend/family and when they visit us, get ${referrerBenefit} benefit on each referral`]
            || `Refer your friend/family and when they visit us, get ${referrerBenefit} benefit on each referral`;
    }, [languageData, referrerBenefit]);

    useEffect(() => {
        if (!userData?.id || !userData?.tenantId) return;

        getOrderByTenantIdAndGuestId(userData.id, userData.tenantId)
            .then((orders: any) => {
                const orderList = Array.isArray(orders) ? orders : Array.isArray(orders?.data) ? orders.data : [];
                if (!orderList.length) {
                    navigateTo(REFERRAL_ROUTE);
                }
            })
            .catch(() => {
                navigateTo(REFERRAL_ROUTE);
            });
    }, [userData?.id, userData?.tenantId]);

    useEffect(() => {
        if (!tenantId || !storeId) return;

        getReferralConfig(tenantId, storeId)
            .then((config) => setReferralConfig(config))
            .catch(() => setReferralConfig(null));
    }, [tenantId, storeId]);

    useEffect(() => {
        if (!isPhoneSetupReady) return;

        setFriends((prev) => {
            if (!prev.length) {
                return createInitialFriends(defaultCountryCode);
            }

            return prev.map((friend) => (
                friend.mobileNo
                    ? friend
                    : { ...friend, countryCode: defaultCountryCode }
            ));
        });
    }, [defaultCountryCode, isPhoneSetupReady]);

    const isValidMobile = useCallback((mobileNo: string) => {
        if (!mobileNo) return false;
        if (!basicConfig?.multipleCountryCode) {
            if (storeMetaData?.phoneNoLength) {
                return Number(storeMetaData.phoneNoLength) === mobileNo.length;
            }
            return mobileNo.length >= 5 && mobileNo.length <= 15;
        }
        return mobileNo.length >= 5 && mobileNo.length <= 15;
    }, [basicConfig?.multipleCountryCode, storeMetaData?.phoneNoLength]);

    // Unused cards (blank name + no real mobile) are skipped — user can submit with 1 or 2 of 3 filled
    const isFriendEmpty = useCallback((friend: ReferralFriendInput) => {
        const name = (friend.name || '').trim();
        const mobile = (friend.mobileNo || '').trim();
        if (name) return false;
        // PhoneInput can leave a short dial-code remnant; treat that as unused too
        return mobile.length < 5;
    }, []);

    const isFriendComplete = useCallback((friend: ReferralFriendInput) => (
        Boolean((friend.name || '').trim()) && isValidMobile(friend.mobileNo)
    ), [isValidMobile]);

    const getCompleteFriends = useCallback((friendList: ReferralFriendInput[]) => (
        friendList.filter(isFriendComplete)
    ), [isFriendComplete]);

    const duplicateMobileErrorMessage = languageData["Same mobile number cannot be used for more than one friend."]
        || "Same mobile number cannot be used for more than one friend.";
    const existingGuestErrorMessage = languageData["This mobile number is already registered with us."]
        || "This mobile number is already registered with us.";

    const getDuplicateMobileError = useCallback((friendList: ReferralFriendInput[]) => {
        // Only compare numbers that have reached full valid length (e.g. 10 digits)
        const validMobiles = friendList
            .map((friend) => (friend.mobileNo || '').trim())
            .filter((mobileNo) => isValidMobile(mobileNo));

        const hasDuplicateMobile = validMobiles.some(
            (mobileNo, index) => validMobiles.indexOf(mobileNo) !== index
        );

        return hasDuplicateMobile ? duplicateMobileErrorMessage : '';
    }, [duplicateMobileErrorMessage, isValidMobile]);

    const getExistingGuestFormError = useCallback((
        friendList: ReferralFriendInput[],
        existingMap: Record<string, string>
    ) => {
        const hasExistingGuest = friendList.some((friend) => (
            existingMap[friend.id] && existingMap[friend.id] === (friend.mobileNo || '').trim()
        ));
        return hasExistingGuest ? existingGuestErrorMessage : '';
    }, [existingGuestErrorMessage]);

    const syncValidationError = useCallback((
        friendList: ReferralFriendInput[],
        existingMap: Record<string, string>
    ) => {
        const duplicateError = getDuplicateMobileError(friendList);
        if (duplicateError) {
            setFormError(duplicateError);
            return;
        }
        setFormError(getExistingGuestFormError(friendList, existingMap));
    }, [getDuplicateMobileError, getExistingGuestFormError]);

    const checkGuestExistsInCrm = useCallback((friendId: string, mobileNo: string, friendList: ReferralFriendInput[]) => {
        if (!tenantId || !storeId || !isValidMobile(mobileNo)) return;

        guestCheckRequestRef.current[friendId] = mobileNo;

        getUserByTenantAndMobile(tenantId, storeId, mobileNo, true)
            .then((response: any) => {
                if (guestCheckRequestRef.current[friendId] !== mobileNo) return;

                setExistingGuestMobiles((prev) => {
                    const next = { ...prev };

                    if (response?.data?.mobileNo) {
                        next[friendId] = mobileNo;
                    } else {
                        delete next[friendId];
                    }

                    syncValidationError(friendList, next);
                    return next;
                });
            })
            .catch(() => {
                if (guestCheckRequestRef.current[friendId] !== mobileNo) return;
                // Keep form usable if CRM lookup fails; submit will re-check
            });
    }, [isValidMobile, storeId, syncValidationError, tenantId]);

    const updateFriend = (id: string, field: keyof ReferralFriendInput, value: string) => {
        setFriends((prev) => prev.map((friend) => (
            friend.id === id ? { ...friend, [field]: value } : friend
        )));
        setFormError((prev) => (
            prev === duplicateMobileErrorMessage || prev === existingGuestErrorMessage ? prev : ''
        ));
    };

    const onPhoneChange = (id: string, mobileNo: string, countryCode = defaultCountryCode) => {
        const dialCode = (countryCode || '').toString();
        // Strip dial code if PhoneInput left it in the mobile segment
        let sanitizedMobile = mobileNo.startsWith(dialCode) ? mobileNo.slice(dialCode.length) : mobileNo;
        sanitizedMobile = sanitizedMobile.startsWith('0') ? sanitizedMobile.replace(/^0+/, '') : sanitizedMobile;
        const lastChar = sanitizedMobile.charAt(sanitizedMobile.length - 1);

        if ((lastChar && lastChar !== ' ' && !isNaN(Number(lastChar))) || !sanitizedMobile.length) {
            const nextFriends = friends.map((friend) => (
                friend.id === id ? { ...friend, mobileNo: sanitizedMobile, countryCode: dialCode } : friend
            ));
            setFriends(nextFriends);

            const nextExistingMap = { ...existingGuestMobiles };
            if (!isValidMobile(sanitizedMobile) || nextExistingMap[id] !== sanitizedMobile) {
                delete nextExistingMap[id];
                setExistingGuestMobiles(nextExistingMap);
            }

            const duplicateError = getDuplicateMobileError(nextFriends);
            if (duplicateError) {
                setFormError(duplicateError);
                return;
            }

            if (isValidMobile(sanitizedMobile)) {
                // Show CRM result as soon as full number is entered
                checkGuestExistsInCrm(id, sanitizedMobile, nextFriends);
            } else {
                syncValidationError(nextFriends, nextExistingMap);
            }
        }
    };

    const addMoreFriend = () => {
        setFriends((prev) => [...prev, createEmptyFriend(defaultCountryCode, prev.length)]);
    };

    const removeFriend = (id: string) => {
        if (friends.length === 1) return;
        const nextFriends = friends.filter((friend) => friend.id !== id);
        const nextExistingMap = { ...existingGuestMobiles };
        delete nextExistingMap[id];
        delete guestCheckRequestRef.current[id];
        setFriends(nextFriends);
        setExistingGuestMobiles(nextExistingMap);
        syncValidationError(nextFriends, nextExistingMap);
    };

    const validateFriends = () => {
        // Only validate cards the user started filling; blank 2nd/3rd/etc. cards are ignored
        for (const friend of friends) {
            if (isFriendEmpty(friend) || isFriendComplete(friend)) continue;

            if (!(friend.name || '').trim()) {
                return languageData["Please enter friend's name."] || "Please enter friend's name.";
            }
            if (!isValidMobile(friend.mobileNo)) {
                return languageData["Please enter a valid mobile number."] || "Please enter a valid mobile number.";
            }
        }

        const completeFriends = getCompleteFriends(friends);
        if (!completeFriends.length) {
            return languageData["Please fill at least one friend's details."] || "Please fill at least one friend's details.";
        }

        const duplicateError = getDuplicateMobileError(friends);
        if (duplicateError) return duplicateError;

        return getExistingGuestFormError(friends, existingGuestMobiles);
    };

    const validateFriendsAgainstCrm = async (friendList: ReferralFriendInput[]) => {
        if (!tenantId || !storeId) {
            return languageData["Unable to validate mobile numbers. Please try again."]
                || "Unable to validate mobile numbers. Please try again.";
        }

        for (const friend of friendList) {
            try {
                const response: any = await getUserByTenantAndMobile(tenantId, storeId, friend.mobileNo, true);
                if (response?.data?.mobileNo) {
                    setExistingGuestMobiles((prev) => ({ ...prev, [friend.id]: friend.mobileNo }));
                    return existingGuestErrorMessage;
                }
            } catch {
                return languageData["Unable to validate mobile numbers. Please try again."]
                    || "Unable to validate mobile numbers. Please try again.";
            }
        }

        return '';
    };

    const buildPayload = useCallback((friendList: ReferralFriendInput[]): GuestReferralInvitePayload[] => {
        const referredByName = [userData?.firstName, userData?.lastName].filter(Boolean).join(' ').trim();

        return friendList.map((friend) => ({
            referredByNumber: userData?.mobileNo || '',
            referredByName,
            referredByCountryCode: userData?.countryCode?.toString() || defaultCountryCode,
            referredToName: friend.name.trim(),
            referredToNumber: friend.mobileNo,
            referredToCountryCode: friend.countryCode || defaultCountryCode,
            referralCode: userData?.referralCode || '',
            referredByGuestId: userData?.id || '',
            tenantId: userData?.tenantId,
            storeId: userData?.storeId,
        }));
    }, [defaultCountryCode, userData]);

    const handleSubmit = async () => {
        if (!userData?.id) {
            setFormError(languageData["Please log in to send invitations."] || "Please log in to send invitations.");
            return;
        }

        const validationError = validateFriends();
        if (validationError) {
            setFormError(validationError);
            return;
        }

        const submitted = getCompleteFriends(friends).map((friend) => ({
            ...friend,
            name: friend.name.trim(),
        }));

        setIsSubmitting(true);
        setFormError('');
        dispatch(enableLoader());

        const crmError = await validateFriendsAgainstCrm(submitted);
        if (crmError) {
            setFormError(crmError);
            setIsSubmitting(false);
            dispatch(disableLoader());
            return;
        }

        const payload = buildPayload(submitted);

        submitGuestReferralInvites(payload)
            .then(() => {
                navigateTo(REFERRAL_ROUTE);
            })
            .catch(() => {
                setFormError(languageData["Unable to send invitations. Please try again."] || "Unable to send invitations. Please try again.");
            })
            .finally(() => {
                setIsSubmitting(false);
                dispatch(disableLoader());
            });
    };

    return (
        <div className="referral-page-outer referral-add-page">
            <div className="referral-add-scroll">
                <div className="referral-add-hero">
                    <div className="referral-network-wrap">
                        <ReferralNetworkIcon />
                    </div>
                    <p className="referral-add-hero-message">{referralOfferMessage}</p>
                </div>

                <div className="referral-form-wrap">
                    {formError && <p className="referral-form-error">{formError}</p>}

                    {friends.map((friend, index) => (
                        <div className="referral-friend-card" key={friend.id}>
                            <div className="referral-friend-card-header">
                                <span className="referral-friend-label">
                                    # {index + 1}
                                </span>
                                {friends.length > 1 && (
                                    <button
                                        type="button"
                                        className="referral-remove-btn"
                                        onClick={() => removeFriend(friend.id)}
                                    >
                                        <IconFrame><CloseIcon /></IconFrame>
                                    </button>
                                )}
                            </div>

                            <div className="referral-input-group">
                                <input
                                    id={`friend-name-${friend.id}`}
                                    className="referral-input"
                                    type="text"
                                    value={friend.name}
                                    onChange={(e) => updateFriend(friend.id, 'name', e.target.value)}
                                    placeholder={languageData["Enter name"] ? languageData["Enter name"] : "Enter name"}
                                />
                            </div>

                            <div className="referral-input-group">
                                {basicConfig?.multipleCountryCode ? (
                                    isPhoneSetupReady && (
                                        <PhoneInput
                                            key={`${friend.id}-${defaultCountry}-${defaultCountryCode}`}
                                            containerClass="referral-phone-container"
                                            inputClass="referral-phone-input"
                                            value={`${friend.countryCode || defaultCountryCode}${friend.mobileNo || ''}`}
                                            onChange={(phone: string, country: any) => onPhoneChange(
                                                friend.id,
                                                phone.replace(country.dialCode, ''),
                                                country.dialCode
                                            )}
                                            countryCodeEditable={false}
                                            placeholder={languageData["Enter mobile number"] ? languageData["Enter mobile number"] : "Enter mobile number"}
                                            country={defaultCountry}
                                            preferredCountries={[defaultCountry]}
                                            enableSearch={true}
                                            inputProps={{ id: `friend-mobile-${friend.id}` }}
                                        />
                                    )
                                ) : (
                                    <div className="referral-phone-single-wrap">
                                        {Boolean(defaultCountryCode) && (
                                            <span className="referral-phone-prefix">+{defaultCountryCode}</span>
                                        )}
                                        <input
                                            id={`friend-mobile-${friend.id}`}
                                            className="referral-input referral-input-with-prefix"
                                            type="tel"
                                            value={friend.mobileNo}
                                            onChange={(e) => onPhoneChange(friend.id, e.target.value, defaultCountryCode)}
                                            maxLength={storeMetaData?.phoneNoLength ? Number(storeMetaData.phoneNoLength) : 10}
                                            placeholder={languageData["Enter mobile number"] ? languageData["Enter mobile number"] : "Enter mobile number"}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="referral-form-actions referral-form-actions-fixed">
                <button type="button" className="referral-btn referral-btn-outline" onClick={addMoreFriend}>
                    {languageData["Add More"] ? languageData["Add More"] : "Add More"}
                </button>
                <button type="button" className="referral-btn" onClick={handleSubmit} disabled={isSubmitting}>
                    {languageData["Send Invitation"] ? languageData["Send Invitation"] : "Send Invitation"}
                </button>
            </div>
        </div>
    );
}
export default AddReferralPage;
